import React, { useState } from "react";
import { useBloodDemand } from "../../../hooks/useDemandAnalytics";
import BarChart from "../../../components/admin/BarChart";
import HorizontalBar from "../../../components/admin/HorizontalBar";
import StatCard from "../../../components/admin/StatCard";
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const PIE_COLORS = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const BloodDemandTab = () => {
  const [days, setDays] = useState(90);
  const { data, isLoading } = useBloodDemand(days);

  if (isLoading) {
    return <div className="skeleton h-64 rounded-lg"></div>;
  }

  const d = data?.data;
  if (!d) return <div className="text-gray-500">No data available</div>;

  const groupChartData = (d.demandByGroup || []).map((item) => ({
    name: item.group,
    value: item.count,
  }));

  const pieData = [
    { name: "Fulfilled", value: d.fulfillmentRate?.fulfilled || 0 },
    { name: "Open", value: d.fulfillmentRate?.open || 0 },
    { name: "Cancelled", value: d.fulfillmentRate?.cancelled || 0 },
    { name: "Closed", value: d.fulfillmentRate?.closed || 0 },
  ].filter((i) => i.value > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Period:</span>
        <select
          className="select select-bordered select-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Requests"
          value={d.fulfillmentRate?.total?.toLocaleString()}
          icon="fa-droplet"
          color="bg-red-50"
          textColor="text-red-600"
        />
        <StatCard
          title="Fulfillment Rate"
          value={`${d.fulfillmentRate?.rate || 0}%`}
          icon="fa-check"
          color="bg-green-50"
          textColor="text-green-600"
        />
        <StatCard
          title="Unfulfilled"
          value={(d.fulfillmentRate?.open || 0).toLocaleString()}
          icon="fa-clock"
          color="bg-yellow-50"
          textColor="text-yellow-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Demand by Blood Group</h2>
            <BarChart
              data={groupChartData}
              labelKey="name"
              valueKey="value"
              color="bg-red-500"
              height="h-48"
            />
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Fulfillment Status</h2>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 py-8">No data</div>
            )}
          </div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Weekly Trend (Last 12 Weeks)</h2>
          {(d.weeklyTrend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ReBarChart data={d.weeklyTrend.map((w) => ({ week: w.week?.slice(-5) || w.week, count: w.count }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </ReBarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-400 py-8">No trend data</div>
          )}
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Demand by District</h2>
          <HorizontalBar
            items={(d.demandByDistrict || []).map((item) => ({
              label: item.district,
              value: item.count,
            }))}
            color="bg-orange-500"
          />
        </div>
      </div>
    </div>
  );
};

export default BloodDemandTab;
